import {
  Button,
  Input,
  Table,
  Tag,
  Space,
  Card,
  TableColumnsType,
  Modal,
  Select,
} from "antd";
import {
  CalendarOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FC, useEffect, useMemo, useState } from "react";
import { TStaff } from "~/types/staff";
import { useGetAllStaff, useGetStaffById } from "~/services/operation";
import { capitalize, getClientName } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { StaffAvailability } from "~/utils/constants";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useStaffStore } from "../staffs.store";
import AvailabilityCalendar from "./availability-calendar";
import { StaffAvailabilityDetails } from "./staff-availability-details";
import { StaffProfileView } from "./staff-profile-view";

interface Props {}

export const StaffDirectory: FC<Props> = () => {
  const { filters, setFilters, openStaffModal } = useStaffStore();
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [availability, setAvailability] = useState<StaffAvailability | "all">(
    "all"
  );
  const [sortBy, setSortBy] = useState<"name" | "lead" | "driver" | "level">("name");
  const [search] = useDebounceValue(searchTerm, 500);

  const { data: selectedStaff } = useGetStaffById(
    selectedStaffId || "",
    !!selectedStaffId && (isCalendarModalVisible || isDetailsModalVisible)
  );

  useEffect(() => {
    const filters: Record<string, any> = {};
    if (availability !== "all") {
      filters["availability"] = availability;
    }
    filters["search"] = search;
    setFilters(filters);
  }, [availability, search, setFilters]);

  const { data: staffs, isLoading } = useGetAllStaff(filters);

  // Sort staff based on selected sort option
  const sortedStaffs = useMemo(() => {
    if (!staffs?.items) return [];

    const items = [...staffs.items];

    switch (sortBy) {
      case "name":
        return items.sort((a, b) => {
          const nameA = formatStaffName(a).toLowerCase();
          const nameB = formatStaffName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case "lead":
        return items.sort((a, b) => {
          if (a.lead === b.lead) return 0;
          return a.lead ? -1 : 1;
        });
      case "driver":
        return items.sort((a, b) => {
          if (a.driver === b.driver) return 0;
          return a.driver ? -1 : 1;
        });
      case "level":
        return items.sort((a, b) => {
          const levelA =
            ((a.installation ?? 0) +
              (a.packing ?? 0) +
              (a.handling ?? 0) +
              (a.clientRelations ?? 0) +
              (a.teamwork ?? 0)) /
            5;
          const levelB =
            ((b.installation ?? 0) +
              (b.packing ?? 0) +
              (b.handling ?? 0) +
              (b.clientRelations ?? 0) +
              (b.teamwork ?? 0)) /
            5;
          return levelB - levelA; // Descending order
        });
      default:
        return items;
    }
  }, [staffs, sortBy]);

  const openCalendarModal = (staffId?: string) => {
    setSelectedStaffId(staffId || null);
    setIsCalendarModalVisible(true);
  };

  const closeCalendarModal = () => {
    setIsCalendarModalVisible(false);
    setSelectedStaffId(null);
  };

  const openDetailsModal = (staffId: string) => {
    setSelectedStaffId(staffId);
    setIsDetailsModalVisible(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalVisible(false);
    setSelectedStaffId(null);
  };

  const openProfileModal = (staffId: string) => {
    setSelectedStaffId(staffId);
    setIsProfileModalVisible(true);
  };

  const closeProfileModal = () => {
    setIsProfileModalVisible(false);
    setSelectedStaffId(null);
  };

  const getModalTitle = () => {
    if (!selectedStaffId) return "Team Availability Calendar";
    if (selectedStaff) {
      return `${formatStaffName(selectedStaff)}'s Availability Calendar`;
    }
    return "Staff Availability Calendar";
  };

  const getStaffName = () => {
    if (selectedStaff) {
      return formatStaffName(selectedStaff);
    }
    return "Staff";
  };

  const columns: TableColumnsType<TStaff> = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        render: (_, record) => (
          <Space>
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#f0f0f0",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <UserOutlined />
            </div>
            <div>
              <Space>
                <div>
                  {record.user?.firstName}_{record.user?.lastName?.charAt(0)}
                </div>
                {record.lead && <Tag color="purple">L</Tag>}
                {record.driver && <Tag color="orange">D</Tag>}
              </Space>
              <div style={{ fontSize: "12px", color: "#999" }}>
                {record.user?.email}
              </div>
            </div>
          </Space>
        ),
      },
      {
        title: "Name",
        key: "name",
        render: (_, record) => getClientName(record.user),
      },
      {
        title: "Role",
        key: "role",
        dataIndex: ["staffRole", "name"],
      },
      {
        title: "Position",
        dataIndex: "position",
        key: "position",
        render: (position: string) => position || "-",
      },
      {
        title: "Skills & Certifications",
        dataIndex: "skills",
        key: "skills",
        render: (skills: string[]) => (
          <Space>
            {skills?.map((skill, index) => (
              <Tag
                key={index}
                color={
                  skill.includes("Forklift") || skill.includes("OSHA")
                    ? "green"
                    : "blue"
                }
              >
                {skill}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: "Availability Submitted",
        key: "availability",
        render: (_, record) => {
          // Show "Submitted" if staff has submitted availability schedules, "Pending" if not
          if (record.hasSubmittedAvailability) {
            return <Tag color="green">Submitted</Tag>;
          }
          return <Tag color="orange">Pending</Tag>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              icon={<UserOutlined />}
              size="small"
              onClick={() => openProfileModal(record.id)}
              title="View Profile"
            />
            <Button
              icon={<CalendarOutlined />}
              size="small"
              onClick={() => openDetailsModal(record.id)}
              title="View Detailed Availability & Schedule"
            />
          </Space>
        ),
      },
    ],
    [openProfileModal, openDetailsModal]
  );

  return (
    <>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ fontWeight: 500 }}>Staff Directory</h3>
            <Space>
              <Select
                style={{ width: 150 }}
                value={sortBy}
                onChange={setSortBy}
                placeholder="Sort by"
              >
                <Select.Option value="name">A-Z (Name)</Select.Option>
                <Select.Option value="lead">Lead First</Select.Option>
                <Select.Option value="driver">Driver First</Select.Option>
                <Select.Option value="level">Star Level</Select.Option>
              </Select>
              <Input
                placeholder="Search staff..."
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                icon={<CalendarOutlined />}
                onClick={() => openCalendarModal()}
                type="primary"
              >
                View All Availability
              </Button>
            </Space>
          </div>
          <Table
            rowKey={(staff) => staff.id}
            columns={columns}
            dataSource={sortedStaffs}
            pagination={{
              total: staffs?.meta.totalItems,
              pageSize: 10,
              current: filters.page,
              onChange: (page) => setFilters({ page }),
              hideOnSinglePage: true,
            }}
            loading={isLoading}
          />
        </Space>
      </Card>

      <Modal
        title={getModalTitle()}
        open={isCalendarModalVisible}
        onCancel={closeCalendarModal}
        width={800}
        footer={null}
        centered
        destroyOnHidden
        bodyStyle={{ padding: "0" }}
      >
        <AvailabilityCalendar staffId={selectedStaffId || undefined} />
      </Modal>

      {selectedStaffId && (
        <StaffAvailabilityDetails
          staffId={selectedStaffId}
          staffName={getStaffName()}
          visible={isDetailsModalVisible}
          onClose={closeDetailsModal}
        />
      )}

      <StaffProfileView
        staffId={selectedStaffId}
        visible={isProfileModalVisible}
        onClose={closeProfileModal}
      />
    </>
  );
};
